import { Box, Typography, useTheme } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { Fragment } from "react";
import { ALPHA_STEP_RING, TEXT } from "../utils/tokens";

/** 納品済に到達した行の最後の丸だけ、初回描画で 0.9→1 に拡大する。 */
const popIn = keyframes`
  from { transform: scale(0.9); }
  to { transform: scale(1); }
`;

const STEP_LABELS = ["受付", "取得中", "納品済"] as const;

type Kind = "progress" | "delivered";

/**
 * ステータスから段（1〜3）と種別を決める。
 * 未知のステータスは「受付」として扱う。
 */
export const deriveStep = (status: string): { step: number; kind: Kind } => {
  switch (status) {
    case "IN_PROGRESS":
      return { step: 2, kind: "progress" };
    case "DELIVERED":
      return { step: 3, kind: "delivered" };
    default:
      return { step: 1, kind: "progress" };
  }
};

interface Props {
  status: string;
}

/** 現在段の表示名（API の statusLabel ではなく3段の名前に統一する）。 */
export const stepLabel = (status: string): string =>
  STEP_LABELS[deriveStep(status).step - 1];

/** 1段の幅（px）。ラベル「取得中」「納品済」（12px×3字）が収まり、丸をその中央に置く。 */
const STEP_WIDTH = 36;
const CIRCLE = 24;

/**
 * 受付→取得中→納品済の3段ステッパー。閉じた一覧でも進み具合が読める。
 * 丸 24px、線 2px、ラベルは丸の下に 12px。最大幅 360px で左端を住所の左端にそろえる（中央寄せにしない）。
 */
export const ProgressStepper: React.FC<Props> = ({ status }) => {
  const theme = useTheme();
  const white = theme.palette.white.main;
  const teal = theme.palette.primary.main;
  const border = theme.palette.border.main;
  const { step, kind } = deriveStep(status);

  return (
    <Box
      role="progressbar"
      aria-label="ステップ進行状況"
      aria-valuemin={1}
      aria-valuemax={3}
      aria-valuenow={step}
      aria-valuetext={`${stepLabel(status)}（3段階中${step}段階目）`}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        maxWidth: 360,
      }}
    >
      {STEP_LABELS.map((label, index) => {
        const n = index + 1;
        const current = n === step;
        const reached = n <= step;
        const isDelivered = kind === "delivered" && n === 3;
        const done = n < step || isDelivered;
        const bg = isDelivered
          ? theme.palette.success.main
          : reached
          ? teal
          : white;
        const labelColor = isDelivered
          ? theme.palette.success.dark
          : current
          ? theme.palette.dark.main
          : done
          ? theme.palette.text.primary
          : theme.palette.text.secondary;
        return (
          <Fragment key={label}>
            {index > 0 && (
              <Box
                aria-hidden
                sx={{
                  flex: "1 1 auto",
                  height: 2,
                  mt: `${CIRCLE / 2 - 1}px`,
                  mx: 0.5,
                  borderRadius: "999px",
                  backgroundColor: reached ? teal : border,
                }}
              />
            )}
            <Box
              aria-current={current ? "step" : undefined}
              sx={{
                width: STEP_WIDTH,
                flex: "0 0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  boxSizing: "border-box",
                  flexShrink: 0,
                  width: CIRCLE,
                  height: CIRCLE,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: bg,
                  border: reached ? 0 : `2px solid ${border}`,
                  color: reached ? white : theme.palette.text.secondary,
                  boxShadow:
                    current && !isDelivered
                      ? `0 0 0 3px ${alpha(teal, ALPHA_STEP_RING)}`
                      : "none",
                  ...(isDelivered && {
                    animation: `${popIn} 0.3s ease-out`,
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none",
                    },
                  }),
                }}
              >
                {done ? (
                  <CheckIcon aria-hidden sx={{ fontSize: 16 }} />
                ) : (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {n}
                  </Typography>
                )}
              </Box>
              <Typography
                component="span"
                sx={{
                  ...TEXT.label,
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  color: labelColor,
                  fontWeight: current || isDelivered ? 700 : 400,
                }}
              >
                {label}
              </Typography>
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
};

/**
 * 表の行用の小さな進捗。丸12px×3＋線で、現在だけ塗り、右に現在の段の名前を出す。
 * 24pxのステッパーとラベルは表の行の高さに収まらないため、表ではこちらを使う。
 */
export const ProgressDots: React.FC<Props> = ({ status }) => {
  const theme = useTheme();
  const teal = theme.palette.primary.main;
  const border = theme.palette.border.main;
  const { step, kind } = deriveStep(status);
  const color = kind === "delivered" ? theme.palette.success.main : teal;
  return (
    <Box
      component="span"
      role="progressbar"
      aria-label="ステップ進行状況"
      aria-valuemin={1}
      aria-valuemax={3}
      aria-valuenow={step}
      aria-valuetext={`${stepLabel(status)}（3段階中${step}段階目）`}
      sx={{ display: "inline-flex", alignItems: "center", gap: 1.25 }}
    >
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        {STEP_LABELS.map((label, index) => {
          const n = index + 1;
          const reached = n <= step;
          const current = n === step;
          return (
            <Box
              component="span"
              key={label}
              sx={{ display: "inline-flex", alignItems: "center" }}
            >
              {index > 0 && (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 12,
                    height: 2,
                    backgroundColor: reached ? color : border,
                  }}
                />
              )}
              <Box
                component="span"
                aria-hidden
                sx={{
                  boxSizing: "border-box",
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  border: `2px solid ${reached ? color : border}`,
                  backgroundColor: current ? color : theme.palette.white.main,
                }}
              />
            </Box>
          );
        })}
      </Box>
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 600,
          whiteSpace: "nowrap",
          color: kind === "delivered" ? "success.dark" : "text.primary",
        }}
      >
        {stepLabel(status)}
      </Typography>
    </Box>
  );
};
